import { SpotPriceApiResponse, SpotPrice, SpotPricesCacheConfig } from '@/types/spotPrices';

// Configurazione cache
const CACHE_CONFIG: SpotPricesCacheConfig = {
  ttl: 30000, // 30 secondi
  maxRetries: 3,
  retryDelay: 1000, // 1 secondo
};

// Cache in memoria
interface CacheEntry {
  data: { oro: SpotPrice | null; argento: SpotPrice | null };
  timestamp: number;
}

let cache: CacheEntry | null = null;

// API endpoint e token
const API_ENDPOINT = 'https://srv12.molecole.net/appcomprooroquotazioni.php';
const API_TOKEN = 'f3b8a1d9e4c74c6fb14e5d7a2f9c3d8e';

/**
 * Formatta il timestamp in formato human-readable
 */
function formatLastUpdated(timestamp: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - timestamp.getTime();
  const diffSeconds = Math.floor(diffMs / 1000);
  
  if (diffSeconds < 60) {
    return `${diffSeconds} secondi fa`;
  } else if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes} minuti fa`;
  } else {
    const hours = Math.floor(diffSeconds / 3600);
    return `${hours} ore fa`;
  }
}

/**
 * Converte i dati dell'API in formato interno
 */
function transformApiData(apiData: SpotPriceApiResponse): { oro: SpotPrice | null; argento: SpotPrice | null } {
  try {
    const oro: SpotPrice | null = apiData.data.XAU ? {
      metal: 'ORO',
      price: parseFloat(apiData.data.XAU.prezzo),
      currency: '€',
      timestamp: new Date(apiData.data.XAU.data_ora),
      lastUpdated: formatLastUpdated(new Date(apiData.data.XAU.data_ora))
    } : null;

    const argento: SpotPrice | null = apiData.data.XAG ? {
      metal: 'ARGENTO',
      price: parseFloat(apiData.data.XAG.prezzo),
      currency: '€',
      timestamp: new Date(apiData.data.XAG.data_ora),
      lastUpdated: formatLastUpdated(new Date(apiData.data.XAG.data_ora))
    } : null;

    return { oro, argento };
  } catch (error) {
    console.error('[SpotPricesService] Error transforming API data:', error);
    return { oro: null, argento: null };
  }
}

/**
 * Verifica se la cache è ancora valida
 */
function isCacheValid(): boolean {
  if (!cache) return false;
  const now = Date.now();
  return (now - cache.timestamp) < CACHE_CONFIG.ttl;
}

/**
 * Fetch dei prezzi spot dall'API con retry logic
 */
async function fetchSpotPricesFromApi(retryCount = 0): Promise<{ oro: SpotPrice | null; argento: SpotPrice | null }> {
  try {
    console.log('[SpotPricesService] 🔄 Fetching spot prices from API...');
    
    const url = `${API_ENDPOINT}?token=${API_TOKEN}&type=spot`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      // Timeout di 10 secondi
      signal: AbortSignal.timeout(10000)
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const apiData: SpotPriceApiResponse = await response.json();
    
    if (apiData.status !== 'success') {
      throw new Error(`API error: ${apiData.status}`);
    }

    const transformedData = transformApiData(apiData);
    
    // Aggiorna cache
    cache = {
      data: transformedData,
      timestamp: Date.now()
    };

    console.log('[SpotPricesService] ✅ Spot prices fetched successfully:', {
      oro: transformedData.oro?.price,
      argento: transformedData.argento?.price
    });

    return transformedData;
  } catch (error) {
    console.error('[SpotPricesService] ❌ Error fetching spot prices:', error);
    
    // Retry logic
    if (retryCount < CACHE_CONFIG.maxRetries) {
      console.log(`[SpotPricesService] 🔄 Retrying... (${retryCount + 1}/${CACHE_CONFIG.maxRetries})`);
      await new Promise(resolve => setTimeout(resolve, CACHE_CONFIG.retryDelay));
      return fetchSpotPricesFromApi(retryCount + 1);
    }
    
    // Se abbiamo dati in cache, restituiamoli anche se scaduti
    if (cache) {
      console.log('[SpotPricesService] 📦 Using cached data as fallback');
      return cache.data;
    }
    
    // Nessun dato disponibile
    return { oro: null, argento: null };
  }
}

/**
 * Ottiene i prezzi spot (da cache o API)
 */
export async function getSpotPrices(): Promise<{ oro: SpotPrice | null; argento: SpotPrice | null }> {
  // Se la cache è valida, restituisci i dati cached
  if (isCacheValid() && cache) {
    console.log('[SpotPricesService] 📦 Using cached spot prices');
    return cache.data;
  }

  // Altrimenti fetch dall'API
  return fetchSpotPricesFromApi();
}

/**
 * Forza il refresh dei prezzi spot
 */
export async function refreshSpotPrices(): Promise<{ oro: SpotPrice | null; argento: SpotPrice | null }> {
  console.log('[SpotPricesService] 🔄 Force refreshing spot prices...');
  // Invalida la cache
  cache = null;
  return fetchSpotPricesFromApi();
}

/**
 * Ottiene lo stato della cache
 */
export function getCacheStatus(): { hasCache: boolean; isValid: boolean; age: number | null } {
  return {
    hasCache: cache !== null,
    isValid: isCacheValid(),
    age: cache ? Date.now() - cache.timestamp : null
  };
}

/**
 * Pulisce la cache
 */
export function clearCache(): void {
  cache = null;
  console.log('[SpotPricesService] 🗑️ Cache cleared');
}
